import { useState, useEffect } from 'react'
import { obtenerCategorias } from '../../db/categorias'

const VACÍO = { nombre: '', precio: '', stock: '', categoria: '', codigoBarras: '', unidad: 'pieza' }

export default function FormProducto({ productoEditar, categoriaInicial, onGuardar, onCancelar }) {
  const [form, setForm] = useState({ ...VACÍO, categoria: categoriaInicial || '' })
  const [categorias, setCategorias] = useState([])
  const [guardando, setGuardando] = useState(false)
  const [exito, setExito] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    obtenerCategorias().then(setCategorias)
  }, [])

  useEffect(() => {
    if (productoEditar) {
      setForm({
        nombre: productoEditar.nombre || '',
        precio: String(productoEditar.precio ?? ''),
        stock: String(productoEditar.stock ?? ''),
        categoria: productoEditar.categoria || '',
        codigoBarras: productoEditar.codigoBarras || '',
        unidad: productoEditar.unidad || 'pieza',
      })
    } else {
      setForm({ ...VACÍO, categoria: categoriaInicial || '' })
    }
    setError('')
  }, [productoEditar, categoriaInicial])

  function actualizar(campo, valor) {
    setForm(prev => ({ ...prev, [campo]: valor }))
  }

  async function enviar(e) {
    e.preventDefault()
    setError('')
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return }
    const precio = parseFloat(form.precio)
    if (isNaN(precio) || precio < 0) { setError('El precio debe ser un número válido'); return }
    if (form.unidad === 'pieza' && (form.stock === '' || isNaN(parseInt(form.stock, 10)) || parseInt(form.stock, 10) < 0)) {
      setError('El stock es obligatorio para productos por pieza'); return
    }

    const cat = categorias.find(c => c.nombre === form.categoria) || categorias.find(c => c.nombre === 'Otro')
    const stockVal = form.stock !== ''
      ? (form.unidad === 'kg' ? parseFloat(form.stock) : parseInt(form.stock, 10))
      : null

    setGuardando(true)
    try {
      await onGuardar({
        ...productoEditar,
        nombre: form.nombre.trim(),
        precio,
        stock: stockVal,
        unidad: form.unidad,
        categoria: form.categoria || 'Otro',
        emoji: cat?.emoji ?? '📦',
        codigoBarras: form.codigoBarras.trim() || null,
        actualizadoEn: new Date().toISOString(),
      })
      setExito(true) // parent delays 700ms before unmounting
    } catch {
      setError('Error al guardar. Intenta de nuevo.')
    } finally {
      setGuardando(false)
    }
  }

  const esPeso = form.unidad === 'kg'

  return (
    <form onSubmit={enviar} noValidate className="p-4 space-y-4">
      <h2 className="text-xl font-bold text-tienda-text mb-2">
        {productoEditar ? 'Editar producto' : 'Nuevo producto'}
      </h2>

      {error && (
        <div className="bg-tienda-danger/20 border border-tienda-danger/50 rounded-xl p-3 text-tienda-danger text-sm">
          {error}
        </div>
      )}

      {/* Tipo de venta */}
      <div>
        <label className="block text-tienda-muted text-sm mb-2">Tipo de venta</label>
        <div className="flex gap-2">
          {['pieza', 'kg'].map(u => (
            <button
              key={u}
              type="button"
              onClick={() => actualizar('unidad', u)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors
                ${form.unidad === u
                  ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                  : 'bg-tienda-bg text-tienda-muted border-tienda-border'}`}
            >
              {u === 'pieza' ? '📦 Por pieza' : '⚖️ Por peso (kg)'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-tienda-muted text-sm mb-1">Nombre *</label>
        <input
          type="text"
          value={form.nombre}
          onChange={e => actualizar('nombre', e.target.value)}
          placeholder={esPeso ? 'Ej. Jitomate' : 'Ej. Coca-Cola 600ml'}
          className="input-field"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-tienda-muted text-sm mb-1">
            Precio {esPeso ? '(por kg)' : '(MXN)'} *
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.precio}
            onChange={e => actualizar('precio', e.target.value)}
            placeholder="0.00"
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-tienda-muted text-sm mb-1">
            Stock {esPeso ? '(kg)' : '(piezas) *'}
          </label>
          <input
            type="number"
            min="0"
            step={esPeso ? '0.1' : '1'}
            value={form.stock}
            onChange={e => actualizar('stock', e.target.value)}
            placeholder={esPeso ? 'Kg en bodega' : 'Piezas'}
            className="input-field"
          />
        </div>
      </div>

      <div>
        <label className="block text-tienda-muted text-sm mb-1">Categoría</label>
        <select
          value={form.categoria}
          onChange={e => actualizar('categoria', e.target.value)}
          className="input-field appearance-none"
        >
          <option value="">Seleccionar...</option>
          {categorias.map(c => (
            <option key={c._id} value={c.nombre}>{c.emoji} {c.nombre}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-tienda-muted text-sm mb-1">
          Código de barras
          <span className="ml-2 text-xs bg-tienda-bg px-2 py-0.5 rounded-full">opcional</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={form.codigoBarras}
          onChange={e => actualizar('codigoBarras', e.target.value)}
          placeholder="Ej. 7501055300105"
          className="input-field font-mono tracking-widest"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancelar} className="btn-ghost flex-1 text-center">
          Cancelar
        </button>
        <button type="submit" disabled={guardando || exito} className="btn-primary flex-1 text-center">
          {guardando ? 'Guardando...' : exito ? '✓ Guardado' : 'Guardar'}
        </button>
      </div>
    </form>
  )
}
